// src/rms/pages/SettingsTax.jsx
// ============================================================================
// Settings → Tax
// ============================================================================
// Four questions, in the order an operator actually answers them:
//
//   1. Do your prices include tax, and what is your registration number?
//   2. What rates do you charge?          (the library)
//   3. Which rates go together?           (groups, one per jurisdiction)
//   4. What does each group apply to?     (rental, delivery, damage…)
//
// The group editor shows a worked example against $100 as you tick rates. That
// preview is the only defence against a mis-set "compounds" flag: compounding
// is completely invisible until you see the number it produces, and a QST that
// compounds when it should not overcharges every customer by about half a
// percent, quietly, forever.
// ============================================================================

import React, { useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { Field, TextInput, Select, Banner, Modal } from "../components/ui";
import { taxOnNet, parseRatePercent, formatRatePercent } from "../pricing/taxEngine";
import { taxLabelForCountry } from "../i18n/countries";
import taxService, { TAXABLE_LINE_KINDS } from "../data/taxService";
import adEngine from "../ads/adEngine";

function SettingsTax({ operator, taxConfig, updateOperator, reload, setNotice }) {
  const { t, formatMoney } = useRms();
  const [editingRate, setEditingRate] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  // Read straight off taxConfig rather than through `|| []` locals: a fresh
  // array literal every render defeats the useMemo below it.
  const rates = taxConfig.rates;
  const groups = taxConfig.groupRows;
  const links = taxConfig.links;

  const groupOptions = useMemo(
    () => [
      { value: "", label: t("tax.notTaxed", "Not taxed") },
      ...groups.filter((g) => g.is_active !== false).map((g) => ({ value: g.id, label: g.name })),
    ],
    [groups, t]
  );

  const after = async (message) => {
    await reload();
    if (message) setNotice(message);
  };

  return (
    <>
      {/* ---- 1. How you price ------------------------------------------- */}
      <PricingMode operator={operator} updateOperator={updateOperator} setNotice={setNotice} />

      {/* ---- 2. The rate library ----------------------------------------- */}
      <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 className="rms-section-title" style={{ marginBottom: 0 }}>
            {t("tax.rates.title", "Your tax rates")}
          </h2>
          <button type="button" className="rms-btn rms-btn--primary rms-btn--sm" onClick={() => setEditingRate(blankRate(rates))}>
            {t("tax.rates.add", "Add a rate")}
          </button>
        </div>
        <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "6px 0 12px" }}>
          {t("tax.rates.help", "One entry per tax you are registered to collect. Order matters only where a tax is charged on top of another.")}
        </p>

        {rates.length === 0 ? (
          <Banner tone="warn">
            {t("tax.rates.none", "No tax rates yet, so invoices are raised with no tax on them.")}
          </Banner>
        ) : (
          <div className="rms-table-wrap">
            <table className="rms-table">
              <thead>
                <tr>
                  <th>{t("tax.rates.name", "Name")}</th>
                  <th>{t("tax.rates.authority", "Authority")}</th>
                  <th className="rms-num">{t("tax.rates.rate", "Rate")}</th>
                  <th className="rms-num">{t("tax.rates.order", "Order")}</th>
                  <th>{t("tax.rates.compounds", "Compounds")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...rates]
                  .sort((a, b) => (a.sequence ?? 100) - (b.sequence ?? 100))
                  .map((r) => (
                    <tr key={r.id} style={{ opacity: r.is_active === false ? 0.5 : 1 }}>
                      <td>
                        <strong>{r.name}</strong>
                        {r.is_active === false ? (
                          <span style={{ fontSize: 11.5, color: "var(--rms-muted)" }}> · {t("tax.rates.inactive", "not in use")}</span>
                        ) : null}
                        {r.registration_number ? (
                          <div style={{ fontSize: 11.5, color: "var(--rms-muted)" }}>{r.registration_number}</div>
                        ) : null}
                      </td>
                      <td style={{ color: "var(--rms-muted)", fontSize: 12.5 }}>{r.authority || "—"}</td>
                      <td className="rms-num">{formatRatePercent(r.rate_ppm)}%</td>
                      <td className="rms-num" style={{ color: "var(--rms-muted)" }}>{r.sequence}</td>
                      <td style={{ fontSize: 12.5, color: "var(--rms-muted)" }}>
                        {r.compound ? t("tax.rates.onEarlier", "on earlier taxes") : "—"}
                      </td>
                      <td className="rms-num">
                        <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditingRate(r)}>
                          {t("action.edit", "Edit")}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---- 3. Groups ---------------------------------------------------- */}
      <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 className="rms-section-title" style={{ marginBottom: 0 }}>
            {t("tax.groups.title", "Rate groups")}
          </h2>
          <button
            type="button"
            className="rms-btn rms-btn--primary rms-btn--sm"
            disabled={!rates.length}
            onClick={() => setEditingGroup({ id: null, name: "", is_default: !groups.length, rateIds: [] })}
          >
            {t("tax.groups.add", "Add a group")}
          </button>
        </div>
        <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "6px 0 12px" }}>
          {t("tax.groups.help", "A group is the set of taxes charged together in one place — a county, a state, a country. Charges point at a group, not at a rate.")}
        </p>

        {groups.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--rms-muted)" }}>{t("tax.groups.none", "No groups yet.")}</p>
        ) : (
          groups.map((g) => {
            const groupRates = links
              .filter((l) => l.tax_group_id === g.id)
              .map((l) => rates.find((r) => r.id === l.tax_rate_id))
              .filter(Boolean);
            return (
              <div key={g.id} style={{ padding: "10px 0", borderTop: "1px solid var(--rms-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                  <div>
                    <strong style={{ fontSize: 13.5 }}>{g.name}</strong>
                    {g.is_default ? (
                      <span className="rms-pill rms-pill--info" style={{ marginInlineStart: 8 }}>
                        {t("tax.groups.default", "default")}
                      </span>
                    ) : null}
                    <div style={{ fontSize: 12.5, color: "var(--rms-muted)" }}>
                      {groupRates.length
                        ? groupRates.map((r) => `${r.name} ${formatRatePercent(r.rate_ppm)}%`).join(" + ")
                        : t("tax.groups.empty", "no rates in this group")}
                    </div>
                  </div>
                  <div style={{ textAlign: "end", whiteSpace: "nowrap" }}>
                    <Preview rates={groupRates} formatMoney={formatMoney} />
                    <button
                      type="button"
                      className="rms-btn rms-btn--ghost rms-btn--sm"
                      style={{ marginTop: 4 }}
                      onClick={() =>
                        setEditingGroup({
                          id: g.id,
                          name: g.name,
                          is_default: !!g.is_default,
                          rateIds: groupRates.map((r) => r.id),
                        })
                      }
                    >
                      {t("action.edit", "Edit")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* ---- 4. What gets taxed -------------------------------------------- */}
      <section className="rms-card rms-card-pad">
        <h2 className="rms-section-title">{t("tax.categories.title", "What gets taxed")}</h2>
        <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "0 0 12px" }}>
          {t("tax.categories.help", "Different charges are often taxed differently — delivery is a service, the hire itself may not be. Set each one to the group that applies.")}
        </p>
        <div className="rms-field-grid">
          {TAXABLE_LINE_KINDS.map(({ kind, label }) => (
            <Field key={kind} label={t(`tax.kind.${kind}`, label)}>
              <Select
                value={taxConfig.byKind[kind] || ""}
                options={groupOptions}
                placeholder={t("tax.useDefault", "Use the default group")}
                onChange={async (groupId) => {
                  adEngine.recordAction();
                  await taxService.setCategoryGroup(operator, kind, groupId || null, taxConfig.categoryRows);
                  await after(t("tax.saved", "Tax settings saved."));
                }}
              />
            </Field>
          ))}
        </div>
      </section>

      {/* ---- 5. Everything else the invoice carries ----------------------- */}
      <InvoiceDetails operator={operator} updateOperator={updateOperator} setNotice={setNotice} />

      {editingRate ? (
        <RateModal
          draft={editingRate}
          operator={operator}
          links={links}
          onCancel={() => setEditingRate(null)}
          onSaved={async (msg) => {
            setEditingRate(null);
            await after(msg);
          }}
          t={t}
        />
      ) : null}

      {editingGroup ? (
        <GroupModal
          draft={editingGroup}
          operator={operator}
          rates={rates.filter((r) => r.is_active !== false)}
          formatMoney={formatMoney}
          onCancel={() => setEditingGroup(null)}
          onSaved={async (msg) => {
            setEditingGroup(null);
            await after(msg);
          }}
          t={t}
        />
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------------

/**
 * Settings → Tax → what an invoice carries besides the charges.
 *
 * Last on the page on purpose. It is the only section that changes nothing
 * about what a customer is charged -- rates, groups and categories all decide
 * money, and this decides what is printed next to it -- so it should not be the
 * first thing an operator meets when they come here to set a rate up.
 *
 * The tax registration keeps its own field at the top of the section rather
 * than becoming another custom line, because the tax engine reads it, snapshots
 * it onto invoices as issued, and prints it at the foot of the totals. The
 * custom lines below it are for everything we cannot enumerate: a UK company
 * number, a plant hire licence, an insurance reference a corporate customer
 * insists on.
 */
function InvoiceDetails({ operator, updateOperator, setNotice }) {
  const { t } = useRms();

  const [address, setAddress] = useState(operator.business_address || "");
  const [phone, setPhone] = useState(operator.business_phone || "");
  const [email, setEmail] = useState(operator.business_email || "");
  const [showDetails, setShowDetails] = useState(!!operator.show_business_details_on_invoices);
  const [lines, setLines] = useState(() =>
    Array.isArray(operator.invoice_legal_lines) ? operator.invoice_legal_lines : []
  );
  const [busy, setBusy] = useState(false);

  // A tax number is the line most operators add first, so the empty row
  // suggests the term their country uses for it -- "GST/HST No." in Canada,
  // "VAT No." in Britain -- rather than a generic example they have to think
  // past. A placeholder only: nothing is filled in on their behalf.
  const suggestedLabel = taxLabelForCountry(operator.country);

  // "Not needed" is an answer, not a refusal: the section collapses to a single
  // line the operator can reopen. Nothing here is ever required, so hiding it
  // costs them nothing and gets a question they have already answered off a
  // page they will come back to.
  const dismissed = !!operator.invoice_extras_dismissed;

  const setDismissed = async (value) => {
    setBusy(true);
    const result = await updateOperator({ invoice_extras_dismissed: value });
    setBusy(false);
    if (!result.success) setNotice({ tone: "danger", text: result.error });
  };

  const setLine = (i, key, value) =>
    setLines((rows) => rows.map((r, n) => (n === i ? { ...r, [key]: value } : r)));
  const addLine = () => setLines((rows) => [...rows, { label: "", value: "" }]);
  const removeLine = (i) => setLines((rows) => rows.filter((_, n) => n !== i));

  const save = async () => {
    setBusy(true);
    adEngine.recordAction();
    const result = await updateOperator({
      business_address: address.trim() || null,
      business_phone: phone.trim() || null,
      business_email: email.trim() || null,
      show_business_details_on_invoices: !!showDetails,
      // A row with nothing in it is not a line the operator wants printed; it
      // is one they started and abandoned. Dropping empties on save means the
      // "add another" button can be pressed freely without leaving debris on
      // the invoice.
      invoice_legal_lines: lines
        .map((r) => ({ label: (r.label || "").trim(), value: (r.value || "").trim() }))
        .filter((r) => r.label || r.value),
    });
    setBusy(false);
    if (!result.success) {
      setNotice({ tone: "danger", text: result.error });
      return;
    }
    setNotice(t("tax.saved", "Tax settings saved."));
  };

  return (
    <section className="rms-card rms-card-pad" style={{ marginTop: 16 }}>
      <h2 className="rms-section-title">{t("tax.invoice.title", "On your invoices")}</h2>

      <h3 className="rms-section-title" style={{ fontSize: 14 }}>
        {t("tax.invoice.otherQuestion", "Does your business need to display other legal information on your invoicing?")}
      </h3>

      {dismissed ? (
        <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "6px 0 0" }}>
          {t("tax.invoice.dismissed", "You said this isn't needed.")}{" "}
          <button type="button" className="rms-linkish" onClick={() => setDismissed(false)} disabled={busy}>
            {t("tax.invoice.reopen", "Add some anyway")}
          </button>
        </p>
      ) : (
        <>
          <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "6px 0 12px" }}>
            {t(
              "tax.invoice.otherHelp",
              "Your tax registration number, a company registration number, a licence, an insurance reference — anything your trade or your customers expect to see. Give each one a label and a value; both print on your invoices."
            )}
          </p>

          {lines.map((row, i) => (
            <div key={i} className="rms-field-grid" style={{ alignItems: "end" }}>
              <Field label={t("tax.invoice.lineLabel", "Label")}>
                <TextInput
                  value={row.label || ""}
                  onChange={(v) => setLine(i, "label", v)}
                  placeholder={suggestedLabel || t("tax.invoice.lineLabelPlaceholder", "Company No.")}
                />
              </Field>
              <Field label={t("tax.invoice.lineValue", "Number")}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <TextInput value={row.value || ""} onChange={(v) => setLine(i, "value", v)} />
                  <button
                    type="button"
                    className="rms-btn rms-btn--ghost rms-btn--sm"
                    onClick={() => removeLine(i)}
                    aria-label={t("tax.invoice.removeLine", "Remove this line")}
                  >
                    ×
                  </button>
                </div>
              </Field>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={addLine}>
              {lines.length
                ? t("tax.invoice.addAnother", "Add another")
                : t("tax.invoice.addLine", "Add a line")}
            </button>
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setDismissed(true)} disabled={busy}>
              {t("tax.invoice.notNeeded", "Not needed")}
            </button>
          </div>

          <h3 className="rms-section-title" style={{ fontSize: 14 }}>
            {t("tax.invoice.addressTitle", "Your address and contact details")}
          </h3>
          <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "6px 0 12px" }}>
            {t(
              "tax.invoice.addressHelp",
              "Where your business trades from, and how a customer reaches you about a bill. Some trades and company types are required to print these; most are not."
            )}
          </p>

          <Field label={t("tax.invoice.address", "Head office or business address")}>
            <textarea
              className="rms-textarea"
              rows={4}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t("tax.invoice.addressPlaceholder", "Written the way you would write it on an envelope.")}
            />
          </Field>

          <div className="rms-field-grid">
            <Field label={t("tax.invoice.phone", "Phone")}>
              <TextInput value={phone} onChange={setPhone} inputMode="tel" />
            </Field>
            <Field label={t("tax.invoice.email", "Email")}>
              <TextInput value={email} onChange={setEmail} inputMode="email" />
            </Field>
          </div>

          <label className="rms-checkline">
            <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
            {t("tax.invoice.showDetails", "Print my address and contact details on invoices")}
          </label>
        </>
      )}

      <div style={{ marginTop: 16 }}>
        <button type="button" className="rms-btn rms-btn--primary" onClick={save} disabled={busy}>
          {t("action.save", "Save")}
        </button>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------

function PricingMode({ operator, updateOperator, setNotice }) {
  const { t } = useRms();
  const [inclusive, setInclusive] = useState(!!operator.prices_include_tax);

  const save = async () => {
    adEngine.recordAction();
    const result = await updateOperator({ prices_include_tax: inclusive });
    if (!result.success) {
      setNotice({ tone: "danger", text: result.error });
      return;
    }
    setNotice(t("tax.saved", "Tax settings saved."));
  };

  return (
    <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
      <h2 className="rms-section-title">{t("tax.mode.title", "How you price")}</h2>

      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        <label className="rms-checkline">
          <input type="radio" name="rms-price-mode" checked={!inclusive} onChange={() => setInclusive(false)} />
          {t("tax.mode.exclusive", "Prices exclude tax — it is added at checkout")}
        </label>
        <label className="rms-checkline">
          <input type="radio" name="rms-price-mode" checked={inclusive} onChange={() => setInclusive(true)} />
          {t("tax.mode.inclusive", "Prices include tax — the price shown is the price paid")}
        </label>
      </div>

      {inclusive !== !!operator.prices_include_tax ? (
        <Banner tone="warn">
          {t(
            "tax.mode.warning",
            "This changes what every rate in your library means, and what your published prices represent. It does not change any invoice you have already raised."
          )}
        </Banner>
      ) : null}

      <button type="button" className="rms-btn rms-btn--primary" onClick={save}>
        {t("action.save", "Save")}
      </button>
    </section>
  );
}

/** A worked example against 100 units of currency. */
function Preview({ rates, formatMoney }) {
  if (!rates.length) return null;
  const base = 10000;
  const { tax, parts } = taxOnNet(base, rates);
  return (
    <div style={{ fontSize: 12.5, color: "var(--rms-muted-strong)" }}>
      {formatMoney(base)} →{" "}
      {rates.map((r, i) => `${r.name} ${formatMoney(Number(parts[i]))}`).join(" + ")} ={" "}
      <strong>{formatMoney(base + Number(tax))}</strong>
    </div>
  );
}

function blankRate(existing) {
  const highest = existing.reduce((m, r) => Math.max(m, r.sequence ?? 100), 0);
  return { id: null, name: "", authority: "", rate: "", sequence: highest + 10, compound: false, registration_number: "", show_registration_on_invoice: false };
}

function RateModal({ draft: initial, operator, links, onCancel, onSaved, t }) {
  const [draft, setDraft] = useState({
    ...initial,
    rate: initial.rate ?? (initial.rate_ppm != null ? formatRatePercent(initial.rate_ppm) : ""),
  });
  const [error, setError] = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const inUse = (links || []).some((l) => l.tax_rate_id === draft.id);

  const submit = async () => {
    const ratePpm = parseRatePercent(draft.rate);
    if (!draft.name.trim()) return setError(t("tax.rate.needName", "Give the rate a name — it appears on invoices."));
    if (ratePpm === null) return setError(t("tax.rate.badRate", "The rate must be a percentage between 0 and 100."));

    adEngine.recordAction();
    await taxService.saveRate(operator, {
      id: draft.id,
      name: draft.name.trim(),
      authority: draft.authority,
      rate_ppm: ratePpm,
      compound: !!draft.compound,
      sequence: Number(draft.sequence) || 100,
      registration_number: draft.registration_number,
      show_registration_on_invoice: !!draft.show_registration_on_invoice,
      is_active: draft.is_active !== false,
    });
    onSaved(t("tax.rate.saved", "Rate saved."));
  };

  const deactivate = async () => {
    adEngine.recordAction();
    const result = await taxService.removeRate(operator, draft.id, links);
    onSaved(
      result.data?.deactivated
        ? t("tax.rate.deactivated", "That rate is used by a group, so it was switched off rather than deleted.")
        : t("tax.rate.deleted", "Rate deleted.")
    );
  };

  return (
    <Modal
      title={draft.id ? t("tax.rate.edit", "Edit rate") : t("tax.rate.new", "New rate")}
      onClose={onCancel}
      footer={
        <>
          {draft.id ? (
            <button type="button" className="rms-btn rms-btn--ghost" onClick={deactivate}>
              {inUse ? t("tax.rate.switchOff", "Switch off") : t("tax.rate.delete", "Delete")}
            </button>
          ) : null}
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {t("action.save", "Save")}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="rms-field-grid">
        <Field label={t("tax.rates.name", "Name")} required>
          <TextInput value={draft.name} onChange={(v) => set("name", v)} placeholder="VAT" />
        </Field>
        <Field label={t("tax.rates.rate", "Rate")} required help={t("tax.rate.help", "As a percentage. Decimals are fine — 9.975 is exact.")}>
          <TextInput value={draft.rate} onChange={(v) => set("rate", v)} inputMode="decimal" placeholder="20" />
        </Field>
        <Field label={t("tax.rates.authority", "Authority")} help={t("tax.rate.authorityHelp", "Who collects it — Revenu Québec, HMRC, the state.")}>
          <TextInput value={draft.authority || ""} onChange={(v) => set("authority", v)} placeholder="HMRC" />
        </Field>
        <Field label={t("tax.rate.regNumber", "Registration number")} help={t("tax.rate.regHelp", "Only if this tax has its own number, separate from your main one.")}>
          <TextInput value={draft.registration_number || ""} onChange={(v) => set("registration_number", v)} />
        </Field>
        <Field label={t("tax.rates.order", "Order")} help={t("tax.rate.orderHelp", "Lower numbers are charged first. Only matters with compounding.")}>
          <TextInput value={draft.sequence} onChange={(v) => set("sequence", v)} inputMode="numeric" />
        </Field>
      </div>

      {/* Off by default. Most businesses have one number, it belongs to the
          business rather than to a tax, and it already prints once at the foot
          of the invoice -- repeating it against every line is noise. Ticking
          this is for the operator registered with two authorities at once. */}
      <label className="rms-checkline">
        <input
          type="checkbox"
          checked={!!draft.show_registration_on_invoice}
          onChange={(e) => set("show_registration_on_invoice", e.target.checked)}
        />
        {t("tax.rate.showOnInvoice", "Print the authority and registration number on the invoice")}
      </label>
      <div className="rms-help">
        {t("tax.rate.showOnInvoiceHelp", "Beneath this tax's line. Only needed if this tax is collected by a different authority from your main registration.")}
      </div>

      <label className="rms-checkline">
        <input type="checkbox" checked={!!draft.compound} onChange={(e) => set("compound", e.target.checked)} />
        {t("tax.rate.compound", "Charged on top of the taxes before it, not just on the price")}
      </label>
      <div className="rms-help">
        {t("tax.rate.compoundHelp", "This is a tax on tax — the second tax is calculated on the subtotal after the first tax is charged.")}
      </div>
    </Modal>
  );
}

function GroupModal({ draft: initial, operator, rates, formatMoney, onCancel, onSaved, t }) {
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const chosen = rates
    .filter((r) => draft.rateIds.includes(r.id))
    .sort((a, b) => (a.sequence ?? 100) - (b.sequence ?? 100));

  const toggle = (id) =>
    set("rateIds", draft.rateIds.includes(id) ? draft.rateIds.filter((x) => x !== id) : [...draft.rateIds, id]);

  const submit = async () => {
    if (!draft.name.trim()) return setError(t("tax.group.needName", "Give the group a name."));
    adEngine.recordAction();
    await taxService.saveGroup(operator, { id: draft.id, name: draft.name.trim(), is_default: draft.is_default }, draft.rateIds);
    onSaved(t("tax.group.saved", "Group saved."));
  };

  return (
    <Modal
      title={draft.id ? t("tax.group.edit", "Edit group") : t("tax.group.new", "New group")}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {t("action.save", "Save")}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <Field label={t("tax.group.name", "Group name")} required help={t("tax.group.nameHelp", "Where these taxes apply — a county, a state, a country.")}>
        <TextInput value={draft.name} onChange={(v) => set("name", v)} placeholder={t("tax.group.namePlaceholder", "California — Alameda")} />
      </Field>

      <div className="rms-label" style={{ marginTop: 10 }}>{t("tax.group.rates", "Taxes in this group")}</div>
      <div style={{ display: "grid", gap: 2, marginBottom: 10 }}>
        {rates.map((r) => (
          <label key={r.id} className="rms-checkline">
            <input type="checkbox" checked={draft.rateIds.includes(r.id)} onChange={() => toggle(r.id)} />
            {r.name} {formatRatePercent(r.rate_ppm)}%
            {r.compound ? ` · ${t("tax.rates.onEarlier", "on earlier taxes")}` : ""}
          </label>
        ))}
      </div>

      {/* The whole reason this modal exists rather than a plain checklist:
          compounding is invisible until you see the number. */}
      {chosen.length ? (
        <div className="rms-card rms-card-pad" style={{ background: "var(--rms-subtle)", padding: 12 }}>
          <div style={{ fontSize: 12, color: "var(--rms-muted)", marginBottom: 4 }}>
            {t("tax.group.preview", "On a charge of")} {formatMoney(10000)}:
          </div>
          <Preview rates={chosen} formatMoney={formatMoney} />
        </div>
      ) : null}

      <label className="rms-checkline" style={{ marginTop: 10 }}>
        <input type="checkbox" checked={!!draft.is_default} onChange={(e) => set("is_default", e.target.checked)} />
        {t("tax.group.isDefault", "Use this group for anything not set explicitly")}
      </label>
    </Modal>
  );
}

export default SettingsTax;
